import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  IconButton,
  TextField,
  MenuItem,
  Chip,
  Stack,
  Alert,
  Snackbar
} from '@mui/material';
import { DataGrid, GridColDef, GridRowModel, GridTreeNode } from '@mui/x-data-grid';
import {
  Save as SaveIcon,
  Cancel as CancelIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  ExpandMore as ExpandMoreIcon,
  ChevronRight as ChevronRightIcon,
  ArrowUpward as ArrowUpIcon,
  ArrowDownward as ArrowDownIcon,
  PostAdd as AddSiblingIcon,
  AddCircleOutline as AddChildIcon
} from '@mui/icons-material';

interface RootPropsExcelEditorProps {
  open: boolean;
  onClose: () => void;
  rootProps: Record<string, any>;
  onSave: (newRootProps: Record<string, any>) => void;
}

interface HierarchicalRow {
  id: string;
  parentId?: string;
  key: string;
  value: any;
  displayValue: string;
  type: string;
  level: number;
  path: string;
  isExpandable: boolean;
  isExpanded: boolean;
  hasChildren: boolean;
  __moveOrder?: number;
  __originalIndex?: number;
  __validationError?: string;
  __isNewSibling?: boolean;
  __siblingOf?: string;
}

interface ValidationError {
  id: string;
  message: string;
  field: 'key' | 'value' | 'type';
  severity: 'error' | 'warning';
}

interface ValidationResult {
  isValid: boolean;
  errors: ValidationError[];
}

const RootPropsExcelEditor: React.FC<RootPropsExcelEditorProps> = ({
  open,
  onClose,
  rootProps,
  onSave
}) => {
  const [newAndModifiedRows, setNewAndModifiedRows] = useState<HierarchicalRow[]>([]);
  const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [snackbarMessage, setSnackbarMessage] = useState<string>('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  // Create complete hierarchical structure (without expansion state)
  const createCompleteHierarchy = useCallback((
    obj: Record<string, any>,
    parentId?: string,
    parentPath: string = '',
    level: number = 0
  ): HierarchicalRow[] => {
    const rows: HierarchicalRow[] = [];

    Object.entries(obj).forEach(([key, value], index) => {
      const id = parentId ? `${parentId}.${key}` : key;
      const path = parentPath ? `${parentPath}.${key}` : key;
      const isObject = typeof value === 'object' && value !== null && !Array.isArray(value);
      const isArray = Array.isArray(value);
      const hasChildren = isObject || isArray;

      let displayValue: string;
      let type: string;

      if (isArray) {
        displayValue = `Array(${value.length})`;
        type = 'array';
      } else if (isObject) {
        displayValue = `Object(${Object.keys(value).length} props)`;
        type = 'object';
      } else {
        displayValue = String(value);
        type = typeof value;
      }

      const row: HierarchicalRow = {
        id,
        parentId,
        key,
        value,
        displayValue,
        type,
        level,
        path,
        isExpandable: hasChildren,
        isExpanded: false, // Will be updated based on expandedRows
        hasChildren,
        __originalIndex: index
      };

      rows.push(row);

      // Always add all children to maintain complete structure
      if (hasChildren) {
        if (isArray) {
          // Debug searchIndexes array specifically
          if (key === 'searchIndexes' || id === 'searchIndexes') {
            console.log(`=== CREATING SEARCHINDEXES ARRAY CHILDREN ===`);
            console.log(`Parent ID: ${id}, Array length: ${value.length}`);
          }

          value.forEach((item: any, idx: number) => {
            const childId = `${id}[${idx}]`;
            const childPath = `${path}[${idx}]`;
            const itemIsObject = typeof item === 'object' && item !== null && !Array.isArray(item);
            const itemIsArray = Array.isArray(item);
            const itemHasChildren = itemIsObject || itemIsArray;

            let itemDisplayValue: string;
            let itemType: string;

            if (itemIsArray) {
              itemDisplayValue = `Array(${item.length})`;
              itemType = 'array';
            } else if (itemIsObject) {
              itemDisplayValue = `Object(${Object.keys(item).length} props)`;
              itemType = 'object';
            } else {
              itemDisplayValue = String(item);
              itemType = typeof item;
            }

            const childRow = {
              id: childId,
              parentId: id,
              key: `[${idx}]`,
              value: item,
              displayValue: itemDisplayValue,
              type: itemType,
              level: level + 1,
              path: childPath,
              isExpandable: itemHasChildren,
              isExpanded: false, // Will be updated based on expandedRows
              hasChildren: itemHasChildren,
              __originalIndex: idx
            };

            // Debug searchIndexes children specifically
            if (id === 'searchIndexes') {
              console.log(`  Creating child ${idx}: id=${childId}, parentId=${id}, key=${childRow.key}`);
            }

            rows.push(childRow);

            // Recursively add all nested children
            if (itemHasChildren) {
              if (itemIsObject) {
                rows.push(...createCompleteHierarchy(item, childId, childPath, level + 2));
              }
            }
          });
        } else if (isObject) {
          rows.push(...createCompleteHierarchy(value, id, path, level + 1));
        }
      }
    });

    return rows;
  }, []);

  // Memoize the complete hierarchy (only recalculates when rootProps changes)
  const completeHierarchy = useMemo(() => {
    return createCompleteHierarchy(rootProps);
  }, [rootProps, createCompleteHierarchy]);

  // Combine complete hierarchy with new/modified rows and filter for visibility
  const visibleHierarchicalRows = useMemo(() => {
    // Create a map of existing rows for efficient lookup
    const existingRowMap = new Map(completeHierarchy.map(row => [row.id, row]));

    // Apply modifications from newAndModifiedRows
    newAndModifiedRows.forEach(modifiedRow => {
      existingRowMap.set(modifiedRow.id, modifiedRow);
    });

    const isVisible = (row: HierarchicalRow): boolean => {
      if (row.level === 0) return true;

      // For child rows, check if all ancestors are expanded
      if (!row.parentId) return true;

      // Check immediate parent
      const parentIsExpanded = expandedRows.has(row.parentId);
      if (!parentIsExpanded) return false;

      // For deeper nesting, recursively check all ancestors
      const parent = existingRowMap.get(row.parentId);
      if (parent && parent.level > 0) {
        return isVisible(parent);
      }

      return true;
    };

    // Convert map back to array and apply visibility filtering
    const allRows = Array.from(existingRowMap.values())
      .map(row => ({
        ...row,
        isExpanded: expandedRows.has(row.id)
      }));

    console.log('ALL ROWS BEFORE FILTERING:', allRows.length);

    const filteredRows = allRows.filter(isVisible);

    console.log('FILTERED VISIBLE ROWS:', filteredRows.length);
    console.log('VISIBLE ROW IDs:', filteredRows.map(r => r.id));

    // Debug searchIndexes specifically
    const searchIndexesChildren = filteredRows.filter(row => row.parentId === 'searchIndexes');
    if (searchIndexesChildren.length > 0) {
      console.log('=== SEARCHINDEXES CHILDREN DEBUG ===');
      console.log('Total searchIndexes children found:', searchIndexesChildren.length);
      searchIndexesChildren.forEach((child, idx) => {
        console.log(`  ${idx}: ${child.key} -> originalIndex: ${child.__originalIndex}, id: ${child.id}, parentId: ${child.parentId}`);
      });

      // Also check all rows in complete hierarchy that should be searchIndexes children
      const allSearchIndexesChildren = allRows.filter(row => row.parentId === 'searchIndexes');
      console.log('All searchIndexes children in complete hierarchy:', allSearchIndexesChildren.length);
      allSearchIndexesChildren.forEach((child, idx) => {
        const isVisible = filteredRows.some(fr => fr.id === child.id);
        console.log(`  ${idx}: ${child.key} -> originalIndex: ${child.__originalIndex}, id: ${child.id}, visible: ${isVisible}`);
      });
    }

    // Debug the complete structure
    console.log('=== COMPLETE FILTERED ROWS STRUCTURE ===');
    filteredRows.forEach((row, idx) => {
      console.log(`${idx}: ${row.key} (level: ${row.level}, parentId: ${row.parentId}, id: ${row.id})`);
    });

    // Proper hierarchical sorting using path-based approach
    const sortedRows = [...filteredRows];

    // Create a stable sort key for each row based on its hierarchical path
    const getSortKey = (row: HierarchicalRow): string => {
      const pathParts: string[] = [];

      // Build the path from root to this row
      let current = row;
      const visited = new Set<string>();

      while (current) {
        if (visited.has(current.id)) break; // Prevent infinite loops
        visited.add(current.id);

        // For array elements, pad the index for proper string sorting
        if (current.key.match(/^\[(\d+)\]$/)) {
          const match = current.key.match(/^\[(\d+)\]$/);
          const index = match ? parseInt(match[1]) : 0;
          pathParts.unshift(String(index).padStart(6, '0')); // Pad to 6 digits for sorting
        } else {
          // For regular properties, use original index properly formatted
          const idx = current.__originalIndex !== undefined ? current.__originalIndex : 999999;
          // Format as integer part + fractional part separated by a zero-padded integer
          const intPart = Math.floor(idx);
          const fracPart = idx - intPart;
          const formattedIdx = String(intPart).padStart(6, '0') +
                               (fracPart > 0 ? '_' + String(Math.floor(fracPart * 1000000)).padStart(6, '0') : '');
          pathParts.unshift(formattedIdx);
        }

        // Find parent
        if (!current.parentId) break;
        current = filteredRows.find(r => r.id === current.parentId) || null;
        if (!current) break;
      }

      return pathParts.join('.');
    };

    // Sort using the hierarchical path keys
    return sortedRows.sort((a, b) => {
      const aKey = getSortKey(a);
      const bKey = getSortKey(b);

      // Debug newly added children
      if (a.id.includes('newChild_') || b.id.includes('newChild_')) {
        console.log(`Sorting child: ${a.key} (${aKey}) vs ${b.key} (${bKey}) = ${aKey.localeCompare(bKey)}`);
        console.log('A:', a);
        console.log('B:', b);
      }

      return aKey.localeCompare(bKey);
    });
  }, [completeHierarchy, newAndModifiedRows, expandedRows]);

  // Validation utility functions
  const validatePropertyKey = useCallback((key: string, parentId?: string, currentRowId?: string): ValidationError | null => {
    // Check for empty or whitespace-only keys
    if (!key || key.trim() === '') {
      return {
        id: currentRowId || 'unknown',
        message: 'Property key cannot be empty',
        field: 'key',
        severity: 'error'
      };
    }

    // Check for invalid JavaScript property names (basic validation)
    if (!/^[a-zA-Z_$][a-zA-Z0-9_$]*$/.test(key) && !/^\[\d+\]$/.test(key)) {
      if (!key.startsWith('newProperty_') && !key.startsWith('newSibling_')) {
        return {
          id: currentRowId || 'unknown',
          message: 'Property key contains invalid characters. Use letters, numbers, underscore, or dollar sign.',
          field: 'key',
          severity: 'warning'
        };
      }
    }

    // Check for duplicate keys at the same level
    if (parentId) {
      const siblings = visibleHierarchicalRows.filter(row =>
        row.parentId === parentId && row.id !== currentRowId
      );
      const duplicateKey = siblings.find(sibling => sibling.key === key);
      if (duplicateKey) {
        return {
          id: currentRowId || 'unknown',
          message: `Duplicate key '${key}' found at this level`,
          field: 'key',
          severity: 'error'
        };
      }
    } else {
      // Check root level duplicates
      const rootSiblings = visibleHierarchicalRows.filter(row =>
        row.level === 0 && row.id !== currentRowId
      );
      const duplicateKey = rootSiblings.find(sibling => sibling.key === key);
      if (duplicateKey) {
        return {
          id: currentRowId || 'unknown',
          message: `Duplicate root property '${key}' found`,
          field: 'key',
          severity: 'error'
        };
      }
    }

    return null;
  }, [visibleHierarchicalRows]);

  const validateValue = useCallback((value: any, type: string, id: string): ValidationError | null => {
    if (!value && value !== '' && value !== '0' && value !== 'false') {
      return null; // Allow empty values
    }

    try {
      switch (type) {
        case 'number':
          const numValue = Number(value);
          if (isNaN(numValue)) {
            return {
              id,
              message: `'${value}' is not a valid number`,
              field: 'value',
              severity: 'error'
            };
          }
          if (!isFinite(numValue)) {
            return {
              id,
              message: 'Number value must be finite',
              field: 'value',
              severity: 'error'
            };
          }
          break;
        case 'boolean':
          const lowerValue = String(value).toLowerCase();
          if (lowerValue !== 'true' && lowerValue !== 'false' && typeof value !== 'boolean') {
            return {
              id,
              message: `'${value}' is not a valid boolean (use 'true' or 'false')`,
              field: 'value',
              severity: 'error'
            };
          }
          break;
        case 'object':
        case 'array':
          // Skip JSON validation - these are handled by the overall structure validation
          break;
      }
    } catch (error) {
      return {
        id,
        message: `Validation error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        field: 'value',
        severity: 'error'
      };
    }

    return null;
  }, []);

  const showValidationMessage = useCallback((message: string, severity: 'success' | 'error' | 'warning' = 'success') => {
    setSnackbarMessage(message);
    setSnackbarSeverity(severity);
    setSnackbarOpen(true);
  }, []);

  const validateHierarchicalStructure = useCallback((): ValidationError[] => {
    const errors: ValidationError[] = [];
    const maxNestingDepth = 10;

    // Check for maximum nesting depth
    visibleHierarchicalRows.forEach(row => {
      if (row.level > maxNestingDepth) {
        errors.push({
          id: row.id,
          message: `Nesting depth exceeds maximum of ${maxNestingDepth} levels`,
          field: 'key',
          severity: 'warning'
        });
      }
    });

    // Check for circular references (basic check)
    const visited = new Set<string>();
    const checkCircular = (row: HierarchicalRow, ancestors: Set<string>): void => {
      if (ancestors.has(row.id)) {
        errors.push({
          id: row.id,
          message: 'Circular reference detected in hierarchy',
          field: 'key',
          severity: 'error'
        });
        return;
      }

      if (visited.has(row.id)) return;
      visited.add(row.id);

      const children = visibleHierarchicalRows.filter(child => child.parentId === row.id);
      children.forEach(child => {
        checkCircular(child, new Set([...ancestors, row.id]));
      });
    };

    const rootRows = visibleHierarchicalRows.filter(row => row.level === 0);
    rootRows.forEach(root => checkCircular(root, new Set()));

    return errors;
  }, [visibleHierarchicalRows]);

  const validateAllRows = useCallback((): ValidationResult => {
    const errors: ValidationError[] = [];

    // Validate each row
    visibleHierarchicalRows.forEach(row => {
      // Validate key
      const keyError = validatePropertyKey(row.key, row.parentId, row.id);
      if (keyError) {
        errors.push(keyError);
      }

      // Validate value
      const valueError = validateValue(row.displayValue, row.type, row.id);
      if (valueError) {
        errors.push(valueError);
      }
    });

    // Validate hierarchical structure
    const structureErrors = validateHierarchicalStructure();
    errors.push(...structureErrors);

    setValidationErrors(errors);
    return {
      isValid: errors.filter(e => e.severity === 'error').length === 0,
      errors
    };
  }, [visibleHierarchicalRows, validatePropertyKey, validateValue, validateHierarchicalStructure]);

  // Reset state when dialog opens
  useEffect(() => {
    if (open) {
      setNewAndModifiedRows([]);
      setExpandedRows(new Set());
      setValidationErrors([]);
      setSnackbarOpen(false);
    }
  }, [open]);

  const handleExpandToggle = useCallback((rowId: string) => {
    console.log('=================== HANDLE EXPAND TOGGLE CALLED ===================');
    console.log('EXPAND CLICK:', rowId);

    // Find children of this row to see what parentIds they expect
    const row = visibleHierarchicalRows.find(r => r.id === rowId);
    console.log('Expanding row:', row);

    // Find all children that should have this row as parent
    const children = completeHierarchy.filter(r => r.parentId === rowId);
    console.log('Children that should show for parent', rowId, ':', children.map(c => ({ id: c.id, parentId: c.parentId })));

    setExpandedRows(prev => {
      const newSet = new Set(prev);
      if (newSet.has(rowId)) {
        newSet.delete(rowId);
        console.log('COLLAPSED:', rowId);
      } else {
        newSet.add(rowId);
        console.log('EXPANDED:', rowId);
      }
      console.log('ALL EXPANDED ROWS:', Array.from(newSet));
      console.log('=================== HANDLE EXPAND TOGGLE END ===================');
      return newSet;
    });
  }, [visibleHierarchicalRows, completeHierarchy]);

  const handleMoveUp = useCallback((id: string) => {
    const currentVisibleRows = visibleHierarchicalRows;
    const currentIndex = currentVisibleRows.findIndex(row => row.id === id);

    if (currentIndex <= 0) return; // Already at top or not found

    const currentRow = currentVisibleRows[currentIndex];
    const previousRow = currentVisibleRows[currentIndex - 1];

    // Only allow moving within the same parent/level
    if (currentRow.parentId !== previousRow.parentId) return;

    // For move operations, we need to add both rows to newAndModifiedRows
    // with a custom order property to control their sequence
    setNewAndModifiedRows(prev => {
      const updated = [...prev.filter(row => row.id !== id && row.id !== previousRow.id)];

      // Add both rows with swapped order indicators
      updated.push({
        ...currentRow,
        __moveOrder: currentIndex - 0.5 // Move current row to previous position
      });

      updated.push({
        ...previousRow,
        __moveOrder: currentIndex + 0.5 // Move previous row to current position
      });

      return updated;
    });
  }, [visibleHierarchicalRows]);

  const handleMoveDown = useCallback((id: string) => {
    const currentVisibleRows = visibleHierarchicalRows;
    const currentIndex = currentVisibleRows.findIndex(row => row.id === id);

    if (currentIndex < 0 || currentIndex >= currentVisibleRows.length - 1) return; // At bottom or not found

    const currentRow = currentVisibleRows[currentIndex];
    const nextRow = currentVisibleRows[currentIndex + 1];

    // Only allow moving within the same parent/level
    if (currentRow.parentId !== nextRow.parentId) return;

    // For move operations, we need to add both rows to newAndModifiedRows
    // with a custom order property to control their sequence
    setNewAndModifiedRows(prev => {
      const updated = [...prev.filter(row => row.id !== id && row.id !== nextRow.id)];

      // Add both rows with swapped order indicators
      updated.push({
        ...nextRow,
        __moveOrder: currentIndex + 0.5 // Move next row to current position
      });

      updated.push({
        ...currentRow,
        __moveOrder: currentIndex + 1.5 // Move current row to next position
      });

      return updated;
    });
  }, [visibleHierarchicalRows]);

  const handleAddSibling = useCallback((referenceId: string) => {
    const referenceRow = visibleHierarchicalRows.find(row => row.id === referenceId);
    if (!referenceRow) {
      showValidationMessage('Reference row not found', 'error');
      return;
    }

    // Generate unique key for the sibling at the same level
    const siblingKey = `newSibling_${Date.now()}`;
    const siblingPath = referenceRow.parentId
      ? `${referenceRow.parentId.replace(/\[|\]/g, '')}.${siblingKey}`
      : siblingKey;

    // Find all rows that are siblings (same parent, same level) from the COMPLETE hierarchy, not just visible ones
    const completeAllRows = [...completeHierarchy, ...newAndModifiedRows];
    const allRowsAtSameLevel = completeAllRows.filter(row =>
      row.parentId === referenceRow.parentId && row.level === referenceRow.level
    );

    // Sort siblings by their original index to maintain order
    allRowsAtSameLevel.sort((a, b) => (a.__originalIndex || 0) - (b.__originalIndex || 0));

    // Find the reference row position among siblings
    const referenceIndexAmongSiblings = allRowsAtSameLevel.findIndex(row => row.id === referenceId);

    // Use a different approach: base the index on the position in the complete list
    // Find where the reference row appears in the complete sorted hierarchy
    const allCompleteRows = [...completeHierarchy, ...newAndModifiedRows].sort((a, b) => {
      return (a.__originalIndex || 999999) - (b.__originalIndex || 999999);
    });

    const referenceIndexInComplete = allCompleteRows.findIndex(row => row.id === referenceId);
    console.log('DEBUG: Reference index in complete list:', referenceIndexInComplete);

    // Calculate new index to place sibling right after reference
    const referenceOriginalIndex = referenceRow.__originalIndex || 0;

    // Look at the next item in the complete list to determine spacing
    let newSiblingIndex;
    if (referenceIndexInComplete < allCompleteRows.length - 1) {
      const nextItem = allCompleteRows[referenceIndexInComplete + 1];
      const nextOriginalIndex = nextItem.__originalIndex || 999999;

      // If the next item is at the same level (sibling), place between them
      if (nextItem.level === referenceRow.level && nextItem.parentId === referenceRow.parentId) {
        newSiblingIndex = referenceOriginalIndex + (nextOriginalIndex - referenceOriginalIndex) / 2;
      } else {
        // Next item is a child or different level, place with small increment
        newSiblingIndex = referenceOriginalIndex + 0.1;
      }
    } else {
      // Reference is last item, add with increment
      newSiblingIndex = referenceOriginalIndex + 0.1;
    }

    console.log('DEBUG: Creating sibling with index:', newSiblingIndex);
    console.log('DEBUG: Reference row index:', referenceOriginalIndex);
    console.log('DEBUG: All siblings at same level:', allRowsAtSameLevel.map(r => ({ key: r.key, index: r.__originalIndex })));

    const newSibling: HierarchicalRow = {
      id: referenceRow.parentId ? `${referenceRow.parentId}.${siblingKey}` : siblingKey,
      parentId: referenceRow.parentId,
      key: siblingKey,
      value: '',
      displayValue: '',
      type: 'string',
      level: referenceRow.level,
      path: siblingPath,
      isExpandable: false,
      isExpanded: false,
      hasChildren: false,
      __originalIndex: newSiblingIndex,
      __isNewSibling: true, // Mark as new sibling for special handling
      __siblingOf: referenceId // Reference to what this is a sibling of
    };

    console.log('DEBUG: Created new sibling:', newSibling);

    // Preserve current expansion state before adding the sibling
    const currentlyExpanded = new Set(expandedRows);

    setNewAndModifiedRows(prev => {
      const updated = [...prev, newSibling];
      console.log('DEBUG: Updated newAndModifiedRows:', updated);
      return updated;
    });

    // Restore expansion state after the state update
    setTimeout(() => {
      setExpandedRows(currentlyExpanded);
      console.log('DEBUG: Restored expansion state:', Array.from(currentlyExpanded));
    }, 0);

    showValidationMessage('Sibling added successfully', 'success');
  }, [visibleHierarchicalRows, showValidationMessage]);

  const handleAddChild = useCallback((parentId: string) => {
    const parentRow = visibleHierarchicalRows.find(row => row.id === parentId);
    if (!parentRow) {
      showValidationMessage('Parent row not found', 'error');
      return;
    }

    // Only allow adding children to objects and arrays
    if (!parentRow.hasChildren && parentRow.type !== 'object' && parentRow.type !== 'array') {
      showValidationMessage('Can only add children to objects or arrays', 'warning');
      return;
    }

    // Generate unique key for the child
    const timestamp = Date.now();
    let childKey, childId, childPath, displayKey, nextIndex;

    if (parentRow.type === 'array') {
      // For arrays, find the next available index
      const existingIndices = visibleHierarchicalRows
        .filter(row => row.parentId === parentId && row.key.match(/^\[\d+\]$/))
        .map(row => parseInt(row.key.match(/\[(\d+)\]/)[1]))
        .sort((a, b) => a - b);

      nextIndex = existingIndices.length > 0 ? Math.max(...existingIndices) + 1 : 0;
      childKey = `[${nextIndex}]`;
      displayKey = childKey;
      childId = `${parentId}${childKey}`;
      childPath = `${parentRow.path}${childKey}`;
    } else {
      // For objects
      nextIndex = 999999; // Put object properties at the end
      childKey = `newProperty_${timestamp}`;
      displayKey = childKey;
      childId = `${parentId}.${childKey}`;
      childPath = `${parentRow.path}.${childKey}`;
    }

    console.log('Creating child with path:', childPath, 'parentPath:', parentRow.path);

    const newChild: HierarchicalRow = {
      id: childId,
      parentId: parentId,
      key: displayKey,
      value: '',
      displayValue: '',
      type: 'string',
      level: parentRow.level + 1,
      path: childPath,
      isExpandable: false,
      isExpanded: false,
      hasChildren: false,
      __originalIndex: nextIndex // Use the calculated index (array index or 999999 for objects)
    };

    // Add the new child first
    console.log('Adding new child:', newChild);
    console.log('Parent row:', parentRow);
    setNewAndModifiedRows(prev => [...prev, newChild]);

    // Then ensure parent stays expanded (do this after adding the child)
    setTimeout(() => {
      setExpandedRows(prev => {
        const newSet = new Set([...prev, parentId]);
        console.log('Ensuring parent expanded:', parentId, 'All expanded:', Array.from(newSet));
        return newSet;
      });
    }, 0);

    showValidationMessage('Child added successfully', 'success');
  }, [visibleHierarchicalRows, showValidationMessage]);

  const columns: GridColDef[] = useMemo(() => [
    {
      field: 'key',
      headerName: 'Property Key',
      flex: 1,
      minWidth: 250,
      editable: true,
      disableClickEventBubbling: true,
      renderEditCell: (params) => {
        const row = params.row as HierarchicalRow;

        // Don't allow editing of keys for objects/arrays with children
        if (row.hasChildren) {
          return (
            <Typography variant="body2" color="text.secondary">
              {row.key} (cannot rename parent objects)
            </Typography>
          );
        }

        return (
          <TextField
            fullWidth
            size="small"
            value={params.value || ''}
            onChange={(e) => params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value
            })}
            variant="outlined"
          />
        );
      },
      renderCell: (params) => {
        const row = params.row as HierarchicalRow;
        const indent = row.level * 20;

        return (
          <Box sx={{ display: 'flex', alignItems: 'center', pl: `${indent}px` }}>
            {row.isExpandable && (
              <Box
                sx={{
                  mr: 1,
                  p: 0.5,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 0.5,
                  width: 24,
                  height: 24,
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  '&:hover': {
                    backgroundColor: 'primary.dark'
                  }
                }}
              >
                {row.isExpanded ? <ExpandMoreIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
              </Box>
            )}
            <Typography variant="body2" sx={{ fontWeight: row.hasChildren ? 'bold' : 'normal' }}>
              {row.key}
            </Typography>
          </Box>
        );
      }
    },
    {
      field: 'displayValue',
      headerName: 'Value',
      flex: 2,
      minWidth: 300,
      editable: true,
      renderEditCell: (params) => {
        const row = params.row as HierarchicalRow;

        // Don't allow editing of objects/arrays - they should be expanded instead
        if (row.hasChildren) {
          return (
            <Typography variant="body2" color="text.secondary">
              {row.displayValue} (expand to edit children)
            </Typography>
          );
        }

        return (
          <TextField
            fullWidth
            size="small"
            value={params.value || ''}
            onChange={(e) => params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value
            })}
            variant="outlined"
          />
        );
      },
      renderCell: (params) => {
        const row = params.row as HierarchicalRow;

        if (row.hasChildren) {
          return (
            <Typography variant="body2" color="text.secondary">
              {row.displayValue}
            </Typography>
          );
        }

        return (
          <Typography variant="body2">
            {row.displayValue}
          </Typography>
        );
      }
    },
    {
      field: 'type',
      headerName: 'Type',
      width: 120,
      editable: true,
      renderEditCell: (params) => {
        const row = params.row as HierarchicalRow;

        // Don't allow type editing for objects/arrays
        if (row.hasChildren) {
          return (
            <Chip
              label={row.type}
              size="small"
              variant="outlined"
              color="default"
            />
          );
        }

        return (
          <TextField
            select
            fullWidth
            size="small"
            value={params.value}
            onChange={(e) => params.api.setEditCellValue({
              id: params.id,
              field: params.field,
              value: e.target.value
            })}
          >
            <MenuItem value="string">string</MenuItem>
            <MenuItem value="number">number</MenuItem>
            <MenuItem value="boolean">boolean</MenuItem>
          </TextField>
        );
      },
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          variant="outlined"
          color={params.row.hasChildren ? "default" : "primary"}
        />
      )
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 280,
      sortable: false,
      renderCell: (params) => {
        const row = params.row as HierarchicalRow;
        const currentVisibleRows = visibleHierarchicalRows;
        const currentIndex = currentVisibleRows.findIndex(r => r.id === params.id);
        const canMoveUp = currentIndex > 0 &&
                         currentVisibleRows[currentIndex - 1]?.parentId === row.parentId;
        const canMoveDown = currentIndex < currentVisibleRows.length - 1 &&
                           currentVisibleRows[currentIndex + 1]?.parentId === row.parentId;

        return (
          <Stack direction="row" spacing={0.5}>
            <Button
              size="small"
              onClick={() => handleMoveUp(params.id as string)}
              disabled={!canMoveUp}
              sx={{ minWidth: 'auto', px: 0.5 }}
            >
              <ArrowUpIcon fontSize="small" />
            </Button>
            <Button
              size="small"
              onClick={() => handleMoveDown(params.id as string)}
              disabled={!canMoveDown}
              sx={{ minWidth: 'auto', px: 0.5 }}
            >
              <ArrowDownIcon fontSize="small" />
            </Button>
            <Button
              size="small"
              color="primary"
              onClick={() => handleAddSibling(params.id as string)}
              sx={{ minWidth: 'auto', px: 0.5 }}
              title="Add Sibling"
            >
              <AddSiblingIcon fontSize="small" />
            </Button>
            {(row.hasChildren || row.type === 'object' || row.type === 'array') && (
              <Button
                size="small"
                color="secondary"
                onClick={() => handleAddChild(params.id as string)}
                sx={{ minWidth: 'auto', px: 0.5 }}
                title="Add Child"
              >
                <AddChildIcon fontSize="small" />
              </Button>
            )}
            <Button
              size="small"
              color="error"
              onClick={() => handleDeleteRow(params.id as string)}
              disabled={row.level === 0 && !row.id.includes('newSibling_') && !row.id.includes('newProperty_') && !row.id.includes('newChild_')} // Allow deleting newly added rows
              sx={{ minWidth: 'auto', px: 0.5 }}
            >
              <DeleteIcon fontSize="small" />
            </Button>
          </Stack>
        );
      }
    }
  ], [handleExpandToggle, handleMoveUp, handleMoveDown, handleAddSibling, handleAddChild, visibleHierarchicalRows]);

  const handleProcessRowUpdate = useCallback((newRow: GridRowModel) => {
    // Validate the updated row first
    const keyError = validatePropertyKey(newRow.key, newRow.parentId, newRow.id as string);
    const valueError = validateValue(newRow.displayValue, newRow.type, newRow.id as string);

    if (keyError) {
      showValidationMessage(keyError.message, keyError.severity);
      if (keyError.severity === 'error') {
        throw new Error(keyError.message);
      }
    }

    if (valueError) {
      showValidationMessage(valueError.message, valueError.severity);
      if (valueError.severity === 'error') {
        throw new Error(valueError.message);
      }
    }

    let updatedValue = newRow.displayValue;

    // Parse value based on type
    try {
      switch (newRow.type) {
        case 'number':
          updatedValue = Number(newRow.displayValue);
          break;
        case 'boolean':
          updatedValue = newRow.displayValue === 'true' || newRow.displayValue === true;
          break;
        default:
          updatedValue = String(newRow.displayValue);
      }
    } catch (e) {
      updatedValue = String(newRow.displayValue);
      showValidationMessage(`Type conversion error: ${e instanceof Error ? e.message : 'Unknown error'}`, 'error');
    }

    const updatedRow: HierarchicalRow = {
      id: newRow.id as string,
      parentId: newRow.parentId,
      key: newRow.key,
      value: updatedValue,
      displayValue: String(newRow.displayValue),
      type: newRow.type,
      level: newRow.level,
      path: newRow.path,
      isExpandable: newRow.isExpandable,
      isExpanded: newRow.isExpanded,
      hasChildren: newRow.hasChildren
    };

    // Add or update in newAndModifiedRows
    setNewAndModifiedRows(prev => {
      const existingIndex = prev.findIndex(row => row.id === updatedRow.id);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = updatedRow;
        return updated;
      } else {
        return [...prev, updatedRow];
      }
    });

    return newRow;
  }, [validatePropertyKey, validateValue, showValidationMessage]);

  const handleAddRow = useCallback(() => {
    const newRow: HierarchicalRow = {
      id: `newProperty_${Date.now()}`,
      key: 'newProperty',
      value: '',
      displayValue: '',
      type: 'string',
      level: 0,
      path: `newProperty_${Date.now()}`,
      isExpandable: false,
      isExpanded: false,
      hasChildren: false
    };
    // Add to newAndModifiedRows - it will be included in visibleHierarchicalRows
    setNewAndModifiedRows(prev => [...prev, newRow]);
  }, []);

  const handleDeleteRow = useCallback((id: string) => {
    // Mark row as deleted by removing it from newAndModifiedRows or adding a delete marker
    setNewAndModifiedRows(prev => prev.filter(row => row.id !== id));

    // Also add a special delete marker for rows that exist in the original hierarchy
    const existsInOriginal = completeHierarchy.some(row => row.id === id);
    if (existsInOriginal) {
      // For now, we'll just filter them out in visibleHierarchicalRows
      // A more complete solution might use a deletedRows set
      setNewAndModifiedRows(prev => [
        ...prev.filter(row => row.id !== id),
        // Add a special marker to indicate this row should be deleted
        {
          id: `${id}_DELETED`,
          key: '',
          value: null,
          displayValue: '',
          type: 'deleted',
          level: -1,
          path: '',
          isExpandable: false,
          isExpanded: false,
          hasChildren: false,
          parentId: id // Use parentId to reference the deleted row
        }
      ]);
    }
  }, [completeHierarchy]);

  const rebuildObjectFromRows = useCallback(() => {
    // Start with a deep copy of the original rootProps to preserve unchanged nested structures
    const result = JSON.parse(JSON.stringify(rootProps));

    // Helper function to set nested value by path
    const setByPath = (obj: any, path: string, value: any) => {
      const keys = path.split(/[.\[\]]/).filter(k => k !== '');
      let current = obj;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        const nextKey = keys[i + 1];

        if (current[key] === undefined) {
          // Determine if next level should be array or object
          current[key] = /^\d+$/.test(nextKey) ? [] : {};
        }
        current = current[key];
      }

      const finalKey = keys[keys.length - 1];
      current[finalKey] = value;
    };

    // Helper function to delete a property by path
    const deleteByPath = (obj: any, path: string) => {
      const keys = path.split(/[.\[\]]/).filter(k => k !== '');
      let current = obj;

      for (let i = 0; i < keys.length - 1; i++) {
        const key = keys[i];
        if (current[key] === undefined) return;
        current = current[key];
      }

      const finalKey = keys[keys.length - 1];
      delete current[finalKey];
    };

    // Get current root level keys from the original object
    const originalRootKeys = new Set(Object.keys(rootProps));

    // Get root level rows from the visible hierarchy (includes both original and modified)
    const rootLevelRows = visibleHierarchicalRows.filter(row => row.level === 0 && row.key.trim());
    const currentRootKeys = new Set(rootLevelRows.map(row => row.key));

    // Remove any root keys that were deleted in the editor
    originalRootKeys.forEach(key => {
      if (!currentRootKeys.has(key)) {
        delete result[key];
      }
    });

    // Process all rows from visible hierarchy (includes modifications) - but only apply changes to values, preserving structure
    visibleHierarchicalRows.forEach(row => {
      if (row.key.trim() && row.type !== 'deleted') {
        if (!row.hasChildren) {
          // For leaf nodes, update the value
          setByPath(result, row.path, row.value);
        }
        // For parent nodes, we preserve their existing structure
        // unless they're completely new (which would be created by setByPath above)
      }
    });

    // Handle deletions marked in newAndModifiedRows
    newAndModifiedRows.forEach(row => {
      if (row.type === 'deleted' && row.parentId) {
        const deletedId = row.parentId;
        // Find the original row to get its path
        const originalRow = completeHierarchy.find(r => r.id === deletedId);
        if (originalRow && originalRow.path) {
          deleteByPath(result, originalRow.path);
        }
      }
    });

    return result;
  }, [visibleHierarchicalRows, newAndModifiedRows, completeHierarchy, rootProps]);

  const handleSave = useCallback(() => {
    // Validate all data before saving
    const validationResult = validateAllRows();

    if (!validationResult.isValid) {
      const errorCount = validationResult.errors.filter(e => e.severity === 'error').length;
      const warningCount = validationResult.errors.filter(e => e.severity === 'warning').length;

      showValidationMessage(
        `Cannot save: ${errorCount} error(s) and ${warningCount} warning(s) found. Please fix all errors before saving.`,
        'error'
      );
      return;
    }

    const warningCount = validationResult.errors.filter(e => e.severity === 'warning').length;
    if (warningCount > 0) {
      showValidationMessage(`Saved with ${warningCount} warning(s)`, 'warning');
    } else {
      showValidationMessage('Configuration saved successfully!', 'success');
    }

    try {
      const newRootProps = rebuildObjectFromRows();
      onSave(newRootProps);

      // Close after a short delay to let user see the success message
      setTimeout(() => {
        onClose();
      }, 1000);
    } catch (error) {
      showValidationMessage(
        `Save failed: ${error instanceof Error ? error.message : 'Unknown error'}`,
        'error'
      );
    }
  }, [rebuildObjectFromRows, onSave, onClose, validateAllRows, showValidationMessage]);

  const handleCancel = useCallback(() => {
    onClose();
  }, [onClose]);

  const handleProcessRowUpdateError = useCallback((error: any) => {
    console.error('Row update error:', error);
  }, []);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: { minHeight: '70vh' }
      }}
    >
      <DialogTitle>
        <Typography variant="h6">Edit Root Properties</Typography>
        <Typography variant="body2" color="text.secondary">
          Hierarchical property editor with expandable nested objects and arrays.
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Stack spacing={2}>
          <Box>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddRow}
              size="small"
            >
              Add Root Property
            </Button>
          </Box>

          <Box sx={{ height: 500, width: '100%' }}>
            <DataGrid
              rows={visibleHierarchicalRows}
              columns={columns}
              processRowUpdate={handleProcessRowUpdate}
              onProcessRowUpdateError={handleProcessRowUpdateError}
              onRowClick={(params, event) => {
                console.log('=== ROW CLICK INTERCEPTED ===', params.id);
                console.log('Row data:', params.row);

                // Check if this is a click on an expandable row
                const row = params.row as HierarchicalRow;
                if (row.isExpandable) {
                  console.log('EXPANDABLE ROW CLICKED');
                  event.stopPropagation();
                  event.preventDefault();
                  console.log('Calling handleExpandToggle with:', row.id);
                  handleExpandToggle(row.id);
                }
              }}
              hideFooter
              disableRowSelectionOnClick
              disableColumnMenu
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              rowHeight={36}
              sx={{
                '& .MuiDataGrid-cell': {
                  borderBottom: '1px solid rgba(224,224,224,1)'
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'grey.100',
                  fontWeight: 'bold'
                },
                '& .MuiDataGrid-row:hover': {
                  backgroundColor: 'action.hover'
                },
                '& .MuiDataGrid-row:nth-of-type(even)': {
                  backgroundColor: 'grey.50'
                },
                '& .MuiDataGrid-virtualScroller': {
                  // Optimize scrolling performance
                  'overflow-anchor': 'none'
                }
              }}
            />
          </Box>

          {/* Validation Errors Display */}
          {validationErrors.length > 0 && (
            <Box sx={{ mt: 2 }}>
              {validationErrors.slice(0, 5).map((error, index) => (
                <Alert
                  key={`${error.id}-${index}`}
                  severity={error.severity}
                  sx={{ mb: 1 }}
                >
                  <strong>{error.field.toUpperCase()}:</strong> {error.message}
                </Alert>
              ))}
              {validationErrors.length > 5 && (
                <Typography variant="body2" color="text.secondary">
                  ... and {validationErrors.length - 5} more validation issue(s)
                </Typography>
              )}
            </Box>
          )}
        </Stack>
      </DialogContent>

      <DialogActions>
        <Button
          onClick={() => {
            const result = validateAllRows();
            const errorCount = result.errors.filter(e => e.severity === 'error').length;
            const warningCount = result.errors.filter(e => e.severity === 'warning').length;

            if (result.isValid) {
              showValidationMessage(
                warningCount > 0
                  ? `Validation passed with ${warningCount} warning(s)`
                  : 'All validation checks passed!',
                warningCount > 0 ? 'warning' : 'success'
              );
            } else {
              showValidationMessage(
                `Validation failed: ${errorCount} error(s), ${warningCount} warning(s)`,
                'error'
              );
            }
          }}
          color="info"
          variant="outlined"
        >
          Validate
        </Button>
        <Button
          onClick={handleCancel}
          startIcon={<CancelIcon />}
          color="secondary"
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          startIcon={<SaveIcon />}
          variant="contained"
        >
          Save Changes
        </Button>
      </DialogActions>

      {/* Snackbar for validation messages */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity as any}
          variant="filled"
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};

export default RootPropsExcelEditor;